import {createAsyncThunk} from '@reduxjs/toolkit'
import {
  FetchPayslipsRequest,
  FetchPayslipByIdRequest,
  CreatePayslipRequest,
  UpdatePayslipRequest,
  FinalizePayslipRequest,
  MarkPayslipPaidRequest,
  DeletePayslipRequest,
  FetchDeletedPayslipsRequest,
  RestorePayslipRequest,
} from './types'
import {AxiosError} from 'axios'
import axios from 'axios'
import {setupAxios} from '../../setup-axios'
import {
  fetchPayslipsUrl,
  fetchPayslipByIdUrl,
  createPayslipUrl,
  updatePayslipUrl,
  finalizePayslipUrl,
  markPayslipPaidUrl,
  deletePayslipUrl,
  fetchDeletedPayslipsUrl,
  restorePayslipUrl,
} from '../../api'

setupAxios(axios)

export const fetchPayslips = createAsyncThunk(
  'payslip/fetch',
  async (payload: FetchPayslipsRequest, {rejectWithValue}) => {
    try {
      const params = new URLSearchParams()
      if (payload.pageNumber) params.append('pageNumber', payload.pageNumber.toString())
      if (payload.pageCount) params.append('pageCount', payload.pageCount.toString())
      if (payload.sortField) params.append('sortField', payload.sortField)
      if (payload.sortOrder) params.append('sortOrder', payload.sortOrder)
      if (payload.userId) params.append('userId', payload.userId)
      if (payload.month) params.append('month', payload.month.toString())
      if (payload.year) params.append('year', payload.year.toString())
      if (payload.status) params.append('status', payload.status)
      if (payload.search) params.append('search', payload.search)

      const res = await axios.get(`${fetchPayslipsUrl}?${params.toString()}`)
      const {payslips, total, totalPages} = res.data
      return {
        payslips,
        total,
        totalPages,
      }
    } catch (error) {
      const err: any = error as AxiosError
      const message = err.response?.data?.message?.fr || err.response?.data?.message?.en || 'Failed to fetch payslips'
      return rejectWithValue({
        success: false,
        error: message,
      })
    }
  }
)

export const fetchPayslipById = createAsyncThunk(
  'payslip/fetchById',
  async (payload: FetchPayslipByIdRequest, {rejectWithValue}) => {
    try {
      const res = await axios.get(fetchPayslipByIdUrl(payload.id))
      const {payslip} = res.data
      return {
        payslip,
      }
    } catch (error) {
      const err: any = error as AxiosError
      const message = err.response?.data?.message?.fr || err.response?.data?.message?.en || 'Failed to fetch payslip'
      return rejectWithValue({
        success: false,
        error: message,
      })
    }
  }
)

export const createPayslip = createAsyncThunk(
  'payslip/create',
  async (payload: CreatePayslipRequest, {rejectWithValue}) => {
    try {
      const res = await axios.post(createPayslipUrl, payload)
      const {payslip} = res.data
      return {
        payslip,
      }
    } catch (error) {
      const err: any = error as AxiosError
      
      // Enhanced error logging - always log in development
      console.error('❌ Payslip creation error:', {
        message: err.message,
        status: err.response?.status,
        statusText: err.response?.statusText,
        errorData: err.response?.data,
        errorDataString: err.response?.data ? JSON.stringify(err.response?.data, null, 2) : 'No error data',
        payload: JSON.parse(JSON.stringify(payload)), // Ensure serializable
        url: err.config?.url,
        method: err.config?.method,
      })
      
      // Try to extract error message from various possible response formats
      const errorData = err.response?.data
      const status = err.response?.status
      
      let message = 'Failed to create payslip: Unknown error'
      
      // If we have error data from the backend, use it
      if (errorData) {
        // Try multiple possible error message formats
        const errorMessage = 
          errorData?.message?.fr || 
          errorData?.message?.en || 
          errorData?.fr || 
          errorData?.en || 
          errorData?.error?.message ||
          errorData?.error ||
          errorData?.message ||
          (typeof errorData === 'string' ? errorData : null)
        
        if (errorMessage) {
          message = `Failed to create payslip: ${errorMessage}`
        } else if (status) {
          message = `Failed to create payslip: Server error ${status}`
        }
      } else if (err.message && err.message !== 'Server temporarily unavailable') {
        // Use the error message if it's not the generic one
        message = `Failed to create payslip: ${err.message}`
      } else if (status) {
        message = `Failed to create payslip: Server error ${status}`
      }
      
      // Prepare serializable error info (avoid AxiosHeaders which are not serializable)
      const serializableError = process.env.NODE_ENV !== 'production' ? {
        message: err.message,
        response: err.response ? {
          status: err.response.status,
          statusText: err.response.statusText,
          data: err.response.data,
          // Convert headers to plain object to avoid serialization issues
          headers: err.response.headers ? Object.fromEntries(
            Object.entries(err.response.headers).map(([key, value]) => [key, String(value)])
          ) : undefined,
        } : undefined,
        request: err.request ? {
          method: err.config?.method,
          url: err.config?.url,
          data: err.config?.data,
        } : undefined,
        config: err.config ? {
          method: err.config.method,
          url: err.config.url,
          // Convert headers to plain object
          headers: err.config.headers ? (typeof err.config.headers === 'object' 
            ? Object.fromEntries(
                Object.entries(err.config.headers).map(([key, value]) => [key, String(value)])
              )
            : String(err.config.headers)) : undefined,
        } : undefined,
      } : undefined

      return rejectWithValue({
        success: false,
        error: message,
        status: status || undefined,
        data: errorData || undefined,
        fullError: serializableError,
      })
    }
  }
)

export const updatePayslip = createAsyncThunk(
  'payslip/update',
  async (payload: UpdatePayslipRequest, {rejectWithValue}) => {
    try {
      const res = await axios.post(updatePayslipUrl, payload)
      const {payslip} = res.data
      return {
        payslip,
      }
    } catch (error) {
      const err: any = error as AxiosError
      const message = err.response?.data?.message?.fr || err.response?.data?.message?.en || 'Failed to update payslip'
      return rejectWithValue({
        success: false,
        error: message,
      })
    }
  }
)

export const finalizePayslip = createAsyncThunk(
  'payslip/finalize',
  async (payload: FinalizePayslipRequest, {rejectWithValue}) => {
    try {
      const res = await axios.post(finalizePayslipUrl, {id: payload.id})
      const {payslip} = res.data
      return {
        payslip,
      }
    } catch (error) {
      const err: any = error as AxiosError
      const message = err.response?.data?.message?.fr || err.response?.data?.message?.en || 'Failed to finalize payslip'
      return rejectWithValue({
        success: false,
        error: message,
      })
    }
  }
)

export const markPayslipPaid = createAsyncThunk(
  'payslip/markPaid',
  async (payload: MarkPayslipPaidRequest, {rejectWithValue}) => {
    try {
      const res = await axios.post(markPayslipPaidUrl, {id: payload.id})
      const {payslip} = res.data
      return {
        payslip,
      }
    } catch (error) {
      const err: any = error as AxiosError
      const message = err.response?.data?.message?.fr || err.response?.data?.message?.en || 'Failed to mark payslip as paid'
      return rejectWithValue({
        success: false,
        error: message,
      })
    }
  }
)

export const deletePayslip = createAsyncThunk(
  'payslip/delete',
  async (payload: DeletePayslipRequest, {rejectWithValue}) => {
    try {
      // Build request body with hardDelete option (for permanent deletion)
      const requestBody: any = {id: payload.id}
      if (payload.hardDelete) {
        requestBody.hardDelete = true
      }
      // Keep force for backward compatibility (deprecated)
      if (payload.force) {
        requestBody.force = true
      }
      
      const res = await axios.post(deletePayslipUrl, requestBody)
      const {payslip} = res.data
      return {
        payslip,
      }
    } catch (error) {
      const err: any = error as AxiosError
      
      // Enhanced error logging
      if (process.env.NODE_ENV !== 'production') {
        console.error('❌ Delete payslip error:', {
          status: err.response?.status,
          statusText: err.response?.statusText,
          data: err.response?.data,
          payload: payload,
        })
      }
      
      // Try to extract error message from various possible response formats
      const errorData = err.response?.data
      const message = 
        errorData?.message?.fr || 
        errorData?.message?.en || 
        errorData?.fr || 
        errorData?.en || 
        errorData?.error?.message ||
        errorData?.error ||
        errorData?.message ||
        'Failed to delete payslip'
      
      return rejectWithValue({
        success: false,
        error: message,
        status: err.response?.status,
        data: errorData,
      })
    }
  }
)

export const fetchDeletedPayslips = createAsyncThunk(
  'payslip/fetchDeleted',
  async (payload: FetchDeletedPayslipsRequest, {rejectWithValue}) => {
    try {
      // Build query parameters - only include non-empty values
      const params = new URLSearchParams()
      
      // Required pagination parameters (with defaults)
      params.append('pageNumber', (payload.pageNumber || 1).toString())
      params.append('pageCount', (payload.pageCount || 10).toString())
      
      // Optional sorting (with defaults)
      params.append('sortField', payload.sortField || 'updatedAt')
      params.append('sortOrder', payload.sortOrder || 'desc')
      
      // Optional filters (only if provided)
      if (payload.userId && payload.userId.trim()) {
        params.append('userId', payload.userId.trim())
      }
      if (payload.month && payload.month > 0) {
        params.append('month', payload.month.toString())
      }
      if (payload.year && payload.year > 0) {
        params.append('year', payload.year.toString())
      }
      if (payload.status && payload.status.trim()) {
        params.append('status', payload.status.trim())
      }
      if (payload.search && payload.search.trim()) {
        params.append('search', payload.search.trim())
      }

      const url = `${fetchDeletedPayslipsUrl}?${params.toString()}`
      
      const res = await axios.get(url)
      
      // Handle different response formats
      const responseData = res.data
      const payslips = responseData.payslips || responseData.data?.payslips || responseData.data || []
      const total = responseData.total || responseData.data?.total || 0
      const totalPages = responseData.totalPages || responseData.data?.totalPages || 0
      
      return {
        payslips: Array.isArray(payslips) ? payslips : [],
        total: Number(total) || 0,
        totalPages: Number(totalPages) || 0,
      }
    } catch (error) {
      const err: any = error as AxiosError
      console.error('❌ Error fetching deleted payslips:', {
        error: err,
        response: err.response,
        status: err.response?.status,
        statusText: err.response?.statusText,
        data: err.response?.data,
        url: err.config?.url,
      })
      
      const errorData = err.response?.data
      const status = err.response?.status
      
      // Handle 500 error specifically
      if (status === 500) {
        const message = 
          errorData?.message?.fr ||
          errorData?.message?.en ||
          errorData?.fr ||
          errorData?.en ||
          errorData?.error?.message ||
          errorData?.error ||
          errorData?.message ||
          'Server error: Failed to fetch deleted payslips. Please try again later.'
        
        return rejectWithValue({
          success: false,
          error: message,
          status: status,
          data: errorData,
        })
      }
      
      const message =
        errorData?.message?.fr ||
        errorData?.message?.en ||
        errorData?.fr ||
        errorData?.en ||
        errorData?.error?.message ||
        errorData?.error ||
        errorData?.message ||
        'Failed to fetch deleted payslips'
      return rejectWithValue({
        success: false,
        error: message,
        status: status,
        data: errorData,
      })
    }
  }
)

export const restorePayslip = createAsyncThunk(
  'payslip/restore',
  async (payload: RestorePayslipRequest, {rejectWithValue}) => {
    try {
      const res = await axios.post(restorePayslipUrl, {id: payload.id})
      const {payslip} = res.data
      return {
        payslip,
        message: res.data.message,
      }
    } catch (error) {
      const err: any = error as AxiosError
      const errorData = err.response?.data
      const message =
        errorData?.message?.fr ||
        errorData?.message?.en ||
        errorData?.fr ||
        errorData?.en ||
        errorData?.error?.message ||
        errorData?.error ||
        errorData?.message ||
        'Failed to restore payslip'
      return rejectWithValue({
        success: false,
        error: message,
        status: err.response?.status,
        data: errorData,
      })
    }
  }
)

